import React from 'react';
import { Box, Typography, Button, Chip, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
    Search as SearchIcon,
    People as PeopleIcon,
    ArrowForward as ArrowForwardIcon,
    CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const BlogPromotion = () => {
    const { currentUser } = useAuth();

    return (
        <Box sx={{ mt: 6, mb: 4 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 8px 40px rgba(106,137,204,0.15)',
                    border: '1px solid rgba(106,137,204,0.12)',
                }}
            >
                {/* 借り手パネル */}
                <Box
                    sx={{
                        p: { xs: 3, sm: 4 },
                        background: 'linear-gradient(145deg, #6a89cc 0%, #79a8ed 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* 背景装飾 */}
                    <Box sx={{
                        position: 'absolute', top: -30, right: -30,
                        width: 120, height: 120, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)',
                    }} />
                    <Box sx={{
                        position: 'absolute', bottom: -20, left: -20,
                        width: 80, height: 80, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.06)',
                    }} />

                    <Chip
                        label="部屋を探している方へ"
                        size="small"
                        sx={{
                            width: 'fit-content',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            letterSpacing: 0.5,
                        }}
                    />
                    <Typography
                        variant="h5"
                        fontWeight={800}
                        color="#fff"
                        sx={{ lineHeight: 1.3, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}
                    >
                        理想の部屋と<br />ルームメイトを見つける
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.8 }}>
                        全国の物件・ルームメイット候補を無料で検索。こだわり条件で理想のルームシェアを。
                    </Typography>

                    {/* 信頼シグナル */}
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                        {['完全無料', '全国対応', '安心サポート'].map((label) => (
                            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckIcon sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                                    {label}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            component={RouterLink}
                            to="/room-search"
                            sx={{
                                bgcolor: '#fff',
                                color: '#6a89cc',
                                fontWeight: 700,
                                borderRadius: '10px',
                                py: 1.2,
                                boxShadow: '0 4px 15px rgba(0,0,0,0.12)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                                    transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            物件を探す
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<PeopleIcon />}
                            component={RouterLink}
                            to="/renters"
                            sx={{
                                borderColor: 'rgba(255,255,255,0.6)',
                                color: '#fff',
                                fontWeight: 600,
                                borderRadius: '10px',
                                py: 1.2,
                                backdropFilter: 'blur(4px)',
                                '&:hover': {
                                    borderColor: '#fff',
                                    bgcolor: 'rgba(255,255,255,0.12)',
                                    transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            ルームメイトを探す
                        </Button>
                    </Stack>
                </Box>

                {/* 貸し手パネル */}
                <Box
                    sx={{
                        p: { xs: 3, sm: 4 },
                        background: '#fff',
                        borderLeft: { md: '1px solid rgba(106,137,204,0.1)' },
                        borderTop: { xs: '1px solid rgba(106,137,204,0.1)', md: 'none' },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* 背景装飾 */}
                    <Box sx={{
                        position: 'absolute', top: -40, right: -40,
                        width: 140, height: 140, borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(106,137,204,0.06), rgba(121,168,237,0.04))',
                    }} />

                    <Chip
                        label="部屋を貸したい方へ"
                        size="small"
                        sx={{
                            width: 'fit-content',
                            bgcolor: 'rgba(106,137,204,0.1)',
                            color: '#6a89cc',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            letterSpacing: 0.5,
                        }}
                    />
                    <Typography
                        variant="h5"
                        fontWeight={800}
                        sx={{
                            color: '#1a202c',
                            lineHeight: 1.3,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                        }}
                    >
                        あなたの部屋を<br />ルームメイトに開く
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#718096', lineHeight: 1.8 }}>
                        写真と条件を入力するだけ。登録無料で、あなたの条件に合ったルームメイット候補へアプローチできます。
                    </Typography>

                    {/* 信頼シグナル */}
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                        {['掲載無料', '3分で完了', 'いつでも削除可'].map((label) => (
                            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckIcon sx={{ fontSize: '0.9rem', color: '#6a89cc' }} />
                                <Typography variant="caption" sx={{ color: '#4a5568', fontWeight: 500 }}>
                                    {label}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <Box sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            component={RouterLink}
                            to={currentUser ? '/create-listing' : '/register'}
                            sx={{
                                background: 'linear-gradient(135deg, #6a89cc, #79a8ed)',
                                fontWeight: 700,
                                borderRadius: '10px',
                                px: 3,
                                py: 1.2,
                                boxShadow: '0 4px 20px rgba(106,137,204,0.35)',
                                '&:hover': {
                                    boxShadow: '0 8px 25px rgba(106,137,204,0.45)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {currentUser ? '今すぐ無料で掲載する' : '無料登録して掲載する'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default BlogPromotion;
